import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { vatDefaults } from '@/data/defaults'
import { calculateVat } from './calculation'
import { content, faqs } from './content'
import type { VatInputs, VatResult } from './types'

const schema = z.object({
  amount: positiveMoney('Enter an amount').refine((v) => v >= 0, {
    message: 'Amount must be at least 0',
  }),
  direction: z.enum(['add', 'remove']),
  rate: z.number().min(0).max(1),
})

const defaults: VatInputs = {
  amount: 1000,
  direction: 'add',
  rate: vatDefaults.rate,
}

export const vatDefinition: CalculatorDefinition<VatInputs, VatResult> = {
  id: 'vat',
  slug: 'vat-calculator',
  category: 'business',
  title: 'VAT Calculator South Africa',
  shortTitle: 'VAT',
  description: `Add or remove ${vatDefaults.rate * 100}% South African VAT from any amount. Override the rate if needed.`,
  inputs: [
    {
      name: 'amount',
      label: 'Amount',
      type: 'money',
      required: true,
      unit: 'R',
      placeholder: 'e.g. 1 000',
    },
    {
      name: 'direction',
      label: 'I want to',
      type: 'radio',
      required: true,
      options: [
        { value: 'add', label: 'Add VAT' },
        { value: 'remove', label: 'Remove VAT' },
      ],
      defaultValue: 'add',
    },
    {
      name: 'rate',
      label: 'VAT rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.001,
      defaultValue: vatDefaults.rate,
    },
  ],
  defaults,
  schema,
  calculate: calculateVat,
  resultSections: [
    {
      id: 'netAmount',
      label: 'Net amount (excl. VAT)',
      format: 'currency',
      value: (r: VatResult) => r.netAmount,
      highlighted: true,
    },
    {
      id: 'vatAmount',
      label: 'VAT amount',
      format: 'currency',
      value: (r: VatResult) => r.vatAmount,
      highlighted: true,
    },
    {
      id: 'grossAmount',
      label: 'Gross amount (incl. VAT)',
      format: 'currency',
      value: (r: VatResult) => r.grossAmount,
      highlighted: true,
    },
    {
      id: 'vatRate',
      label: 'VAT rate used',
      format: 'percent',
      value: (r: VatResult) => r.vatRate,
    },
  ],
  assumptions: [
    {
      name: vatDefaults.sourceName,
      url: vatDefaults.sourceUrl,
      reviewedAt: '2026-09-11',
      effectiveFrom: vatDefaults.dataDate,
      version: vatDefaults.dataDate,
      notes: `Default VAT rate is ${vatDefaults.rate * 100}%. You can override it for other rates.`,
    },
  ],
  related: ['markup-profit-calculator', 'salary-calculator'],
  seo: {
    title: 'VAT Calculator South Africa',
    description: `Add VAT or remove VAT at the ${vatDefaults.rate * 100}% South African rate with this VAT calculator — see the net amount, VAT portion and gross price.`,
    keywords: ['VAT calculator', 'South Africa VAT', '15% VAT', 'VAT inclusive', 'VAT exclusive'],
  },
  content,
  faqs,
}
